import { forwardRef, useImperativeHandle, useRef } from "react";

/** Imperative handle: seek the player to a timestamp and play. */
export interface YouTubePlayerHandle {
  seekTo(seconds: number): void;
}

/**
 * A privacy-enhanced YouTube embed. `seekTo` drives the player over the iframe
 * postMessage API (enablejsapi), so clicking a transcript line jumps the video
 * without reloading the frame.
 */
export const YouTubeEmbed = forwardRef<
  YouTubePlayerHandle,
  { videoId: string; title: string; start?: number }
>(function YouTubeEmbed({ videoId, title, start }, ref) {
  const frame = useRef<HTMLIFrameElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      seekTo(seconds: number) {
        const win = frame.current?.contentWindow;
        if (win == null) {
          return;
        }
        const command = (func: string, args: readonly number[]) => {
          win.postMessage(
            JSON.stringify({ event: "command", func, args }),
            "*",
          );
        };
        command("seekTo", [Math.max(0, Math.floor(seconds)), 1]);
        command("playVideo", []);
      },
    }),
    [],
  );

  const origin = encodeURIComponent(window.location.origin);
  const startParam =
    start !== undefined && start > 0 ? `&start=${Math.floor(start)}` : "";
  const src = `https://www.youtube-nocookie.com/embed/${videoId}?enablejsapi=1&rel=0&modestbranding=1&origin=${origin}${startParam}`;

  return (
    <div
      className="relative w-full overflow-hidden rounded-sm bg-warm-brown"
      style={{ aspectRatio: "16 / 9" }}
    >
      <iframe
        ref={frame}
        src={src}
        title={title}
        className="absolute inset-0 h-full w-full border-0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    </div>
  );
});
